// Engine tính tiền báo giá (SO-002) — NGUỒN DÙNG CHUNG cho FE.
// MIRROR ĐÚNG backend `src/modules/service-order/helpers/so-calc.helper.ts`.
// ⚠ VAT BÓC NGƯỢC (FB P9 C1–C3): sellingPrice ĐÃ GỒM VAT ⇒ KHÔNG cộng VAT lên trên.
//   SubTotal(dòng)     = unitPrice × qty − discountValue(dòng)          (đã gồm VAT)
//   DiscountAllocation = phân bổ CK bill-level theo tỷ lệ subTotal, cap ≤ subTotal,
//                        phần dư dồn vào dòng subTotal LỚN NHẤT → Σalloc = totalBillDiscount.
//   Base(dòng)         = SubTotal − DiscountAllocation                   (đã gồm VAT)
//   PreVatAmount(dòng) = round2( Base ÷ (1 + vatRate/100) )              (C2)
//   VATAmount(dòng)    = Base − PreVatAmount                             (C3)
//   TotalAfterVAT      = TotalAfterDiscount → TỔNG PHẢI THU (đã gồm VAT)
// BE là nguồn chân lý; FE dùng để hiển thị realtime — phải ra số GIỐNG HỆT BE.

const num = (v) => Number(v) || 0;

/** Làm tròn 2 chữ số thập phân (cột DECIMAL(20,2)). */
export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Map VAT_TYPE ('KCT'/'0%'/'5%'/'8%'/'10%') → thuế suất số (10% → 10). */
export function vatTypeToRate(vatType) {
  if (!vatType) return 0;
  const s = String(vatType).trim();
  if (s.endsWith('%')) {
    const v = parseFloat(s.slice(0, -1));
    return Number.isNaN(v) ? 0 : v;
  }
  return 0;
}

/** Quy đổi chiết khấu ra SỐ TIỀN. PERCENT → base × amount/100; AMOUNT → amount (cap ở base). */
export function resolveDiscount(type, amount, base) {
  const amt = num(amount);
  const b = num(base);
  if (amt <= 0 || b <= 0) return 0;
  const value = type === 'PERCENT' ? (b * amt) / 100 : amt;
  return round2(Math.min(value, b));
}

/**
 * Tính toàn bộ báo giá. Trả { items:[{subTotal,discountAllocation,vatAmount}], totals:{...} }.
 * @param {Array} items  [{ unitPrice, quantity, discountType, discountAmount, vatRate }]
 * @param {Object} header { discountType, discountAmount, discountCouponType, discountCouponAmount }
 */
export function computeQuote(items, header) {
  const list = items || [];

  // 1) SubTotal + chiết khấu từng dòng
  let totalBeforeDiscount = 0;
  let totalItemDiscount = 0;
  const subTotals = [];
  for (const it of list) {
    const gross = round2(num(it.unitPrice) * num(it.quantity));
    const itemDiscount = resolveDiscount(
      it.discountType,
      it.discountAmount,
      gross,
    );
    const subTotal = round2(gross - itemDiscount);
    subTotals.push(subTotal);
    totalBeforeDiscount = round2(totalBeforeDiscount + gross);
    totalItemDiscount = round2(totalItemDiscount + itemDiscount);
  }

  const sumSubTotal = round2(subTotals.reduce((a, b) => a + b, 0));

  // 2) Chiết khấu bill-level + coupon (base = tổng subTotal)
  const billDiscount = resolveDiscount(
    header.discountType,
    header.discountAmount,
    sumSubTotal,
  );
  const couponDiscount = resolveDiscount(
    header.discountCouponType,
    header.discountCouponAmount,
    round2(sumSubTotal - billDiscount),
  );
  const totalBillDiscount = round2(
    Math.min(billDiscount + couponDiscount, sumSubTotal),
  );

  // 3) Phân bổ theo tỷ lệ (cap ≤ subTotal); phần dư dồn vào dòng subTotal lớn nhất còn chỗ.
  const allocations = subTotals.map((st) =>
    sumSubTotal > 0 && totalBillDiscount > 0
      ? Math.min(round2((st / sumSubTotal) * totalBillDiscount), st)
      : 0,
  );
  let residual = round2(
    totalBillDiscount - allocations.reduce((a, b) => a + b, 0),
  );
  if (residual !== 0 && sumSubTotal > 0) {
    const order = subTotals
      .map((_, i) => i)
      .sort((a, b) => subTotals[b] - subTotals[a] || a - b);
    for (const i of order) {
      if (residual === 0) break;
      if (residual > 0) {
        const room = round2(subTotals[i] - allocations[i]);
        const add = Math.min(residual, room);
        if (add > 0) {
          allocations[i] = round2(allocations[i] + add);
          residual = round2(residual - add);
        }
      } else {
        const removable = allocations[i];
        const sub = Math.min(-residual, removable);
        if (sub > 0) {
          allocations[i] = round2(allocations[i] - sub);
          residual = round2(residual + sub);
        }
      }
    }
  }

  // 4) VAT BÓC NGƯỢC từng dòng (FB P9 C2–C3). Base đã gồm VAT ⇒ preVat + vat = Base.
  const resultItems = list.map((it, i) => {
    const subTotal = subTotals[i];
    const allocation = allocations[i];
    const vatRate = num(it.vatRate);
    const base = round2(subTotal - allocation);
    const preVatAmount = round2(base / (1 + vatRate / 100));
    const vatAmount = round2(base - preVatAmount);
    return { subTotal, discountAllocation: allocation, preVatAmount, vatAmount };
  });

  // 5) Tổng phiếu. TotalAfterVAT = TotalAfterDiscount (đã gồm VAT) — KHÔNG cộng thêm TotalVAT.
  const totalDiscount = round2(totalItemDiscount + totalBillDiscount);
  const totalAfterDiscount = round2(totalBeforeDiscount - totalDiscount);
  const totalVAT = round2(resultItems.reduce((a, r) => a + r.vatAmount, 0));
  const totalBeforeVAT = round2(resultItems.reduce((a, r) => a + r.preVatAmount, 0));
  const totalAfterVAT = totalAfterDiscount;

  return {
    items: resultItems,
    totals: {
      totalBeforeDiscount,
      totalItemDiscount,
      totalBillDiscount,
      totalDiscount,
      totalAfterDiscount,
      totalBeforeVAT,
      totalVAT,
      totalAfterVAT,
    },
  };
}
